import { cn } from '../../lib/utils';
import { panelCount, panelMoney, panelPercent } from './panel-format';
import { parseAmount, tierEconomics } from './tier-pricing';

/**
 * The four figures e-conomic prints under a quote line, shown live under the
 * pricing inputs: profit per unit, profit on the line, profit as a share of
 * revenue, and the line total.
 *
 * The percentage here is a MARGIN — profit over revenue, e-conomic's "Avance
 * i %". It is deliberately not the markup box above it, which divides the same
 * profit by cost. On a tier costing 112.13 sold at 171.54 they read 34.6% and
 * 53.0%; showing both, named apart, is what stops one being mistaken for the
 * other.
 */
export function TierEconomicsReadout({
	cost,
	salePrice,
	quantity,
	currency,
	className,
	costOnlyMessage = 'No sale price — this tier will be saved with a cost only and quoted with no price.',
	emptyMessage = 'Enter a cost and a sale price',
}: {
	cost: string;
	/** On a markup-authored tier (the catalogue), derive this with `saleFromMarkup`. */
	salePrice: string;
	quantity: number;
	currency: string;
	className?: string;
	/**
	 * Shown when a cost is set but no sale price is. The default speaks for an
	 * offer tier, where that state blocks sending; the catalogue authors a markup
	 * instead and a row without one is perfectly legitimate, so it says its own
	 * thing.
	 */
	costOnlyMessage?: string;
	/** Shown before there is anything to work from. */
	emptyMessage?: string;
}) {
	const e = tierEconomics(cost, salePrice, quantity);

	/*
	 * A tier with a cost but no sale price is the one state worth shouting about.
	 * The backend derives a price from a markup, a sale price or a fixed amount —
	 * given none of them it stores no price at all, and the tier reaches the quote
	 * showing "—" per unit and nothing in the total. That is occasionally what an
	 * admin wants (an unpriced tier can still carry a delivery), so it is a warning
	 * and not a block, but it must never be reached by silence.
	 */
	const costOnly = !e && !!parseAmount(cost);

	if (!e || e.profitUnit === 0) {
		return (
			<div
				className={cn(
					'rounded-lg border px-3 py-2 text-2xs',
					costOnly
						? 'border-status-warn/40 bg-status-warn-soft text-status-warn'
						: 'border-line-1 bg-surface-fill/50 text-text-4',
					className
				)}
			>
				{costOnly
					? costOnlyMessage
					: e
						? 'No profit on this tier — the sale price equals the cost.'
						: emptyMessage}
			</div>
		);
	}

	return (
		<div
			className={cn(
				'grid grid-cols-2 gap-x-3 gap-y-1 rounded-lg border border-line-1 bg-surface-fill/50 px-3 py-2',
				className
			)}
		>
			<Figure label="Profit / unit">
				<Amount>{panelMoney(e.profitUnit, currency)}</Amount>
			</Figure>
			<Figure label="Profit on tier">
				<Amount>{panelMoney(e.profitTotal, currency)}</Amount>
			</Figure>
			<Figure label="Margin">
				{/* Null at a zero sale price — nothing to divide by. */}
				{e.marginPercentage != null ? (
					<Amount>{panelPercent(e.marginPercentage)}</Amount>
				) : (
					<span className="text-2xs text-text-4">—</span>
				)}
			</Figure>
			<Figure label={`Line total · ${panelCount(quantity)} pcs`}>
				<Amount>{panelMoney(e.lineTotal, currency)}</Amount>
			</Figure>
		</div>
	);
}

function Amount({ children }: { children: React.ReactNode }) {
	return <span className="font-mono text-2xs text-text-3">{children}</span>;
}

function Figure({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<span className="flex items-baseline justify-between gap-2">
			<span className="text-2xs text-text-4">{label}</span>
			{children}
		</span>
	);
}
